import { DeployEnv, Site, SLD, type HeaderService, type ListItem } from '@patsnap-utils/biz';
import { getCurrentLanguage } from '@eureka/language-tool';
import '@patsnap-ui/icon/assets/solid/HistoryRecord.svg';
import '@patsnap-ui/icon/assets/userpackage.svg';
import '@patsnap-ui/icon/assets/solid/rocket.svg';
import '@patsnap-ui/icon/assets/solid/AI.svg';
import '@patsnap-ui/icon/assets/solid/Gift.svg';
import { combineHash, combineUrl } from '@eureka/utils/url';
import { clientDeferred } from '@eureka/utils/client';
import { useLsEditionUsage, usePlg } from '@eureka/shared/edition-upgrade';
import { REGION } from '@eureka/utils/environment';
import CreditsIcon from './assets/credits.svg';

type ILocale = 'cn' | 'en' | 'tw' | 'jp'
export interface CustomLinksOptions {
    checkAgentSettingsPermission?: () => Promise<boolean>;
}

export async function customProductsLinkInUserCenter(headerService: HeaderService, options?: CustomLinksOptions) {
    const locale = getCurrentLanguage() as ILocale;
    const buildin = headerService.userCenterService.beforeRenderMenuItems
    const { isFeiShu } = await clientDeferred.promise

    const { isPersonal } = useLsEditionUsage();
    const { checkIsLsPlg, isPro, isCreditsAvailable, isCountPackageAvailable } = usePlg();
    const isLsPlg = await checkIsLsPlg();

    // 异步检查agent-settings权限
    let hasAgentSettingsPermission = false;
    if (options?.checkAgentSettingsPermission) {
        options.checkAgentSettingsPermission().then(permission => {
            hasAgentSettingsPermission = permission;
        }).catch(() => {
            hasAgentSettingsPermission = false;
        });
    }

    headerService.userCenterService.beforeRenderMenuItems = function beforeRenderMenuItems(items: ListItem[]) {
        let _items = buildin(items);
        _items.splice(1, 0, {
            href: new Site({ sld: SLD.EUREKA, pathname: 'history' }).href,
            label: ({
                cn: '历史记录',
                en: 'History',
                jp: '履歴',
                tw: '歷史記錄',
            })[locale],
            icon: 'SolidHistoryRecord',
            key: 'history',
        })

        // 只在有权限时添加个性化设置菜单
        if (hasAgentSettingsPermission && !isFeiShu) {
            _items.splice(2, 0, {
                href: combineHash(combineUrl(location.origin, 'ls'), 'user-setting/agent-settings'),
                label: ({
                    cn: '个性化设置',
                    en: 'customization',
                    jp: 'エージェント設定',
                    tw: '個性化設置',
                })[locale],
                icon: 'SolidAi',
                key: 'agent-settings',
            })
        }

        if (!isFeiShu) {
            const versionUrl = isLsPlg ? new Site({ sld: SLD.EUREKA, pathname: 'user-setting/package/management' }).href : combineHash(combineUrl(location.origin, 'ls'), 'user-setting/package/ls');

            _items.splice(0, 0, {
                href: versionUrl,
                label: ({
                    cn: '所有版本',
                    en: 'All versions',
                    jp: 'すべてのバージョンになります',
                    tw: '所有版本',
                })[locale],
                icon: 'SolidRocket',
                key: 'version',
            })
        }

        _items.splice(0, 0, {
            href: combineHash(combineUrl(location.origin, 'ls'), 'user-setting/amount'),
            label: ({
                cn: '用量',
                en: 'Usage',
                jp: '利用回数',
                tw: '用量',
            })[locale],
            icon: 'Userpackage',
            key: 'package',
        })

        if (isFeiShu) {
            _items.splice(-2, 2)
        }

        if (isPersonal.value) {
            if (isPro.value) {
                // 添加管理订阅菜单项，跳转到credits页面
                _items.unshift({
                    key: 'credits',
                    label: ({
                        cn: '管理订阅',
                        en: 'Manage Subscription',
                        jp: 'サブスクリプション管理',
                        tw: '管理訂閱',
                    })[locale],
                    href: `${import.meta.env.VITE_EUREKA_URI}/user-setting/package/management`,
                    icon: 'SolidRocket',
                });
            }
            const nonePackages = ['package', 'settings', 'team']
            // _items中key:version 的href为/user-setting/package/management
            const versionItem = _items.find(item => item.key === 'version');
            if (versionItem) {
                // CN 环境个人用户不显示订阅管理，只在海外环境显示
                const isOversea = [DeployEnv.US, DeployEnv.EU].includes(REGION);
                if (isOversea) {
                    versionItem.href = `${import.meta.env.VITE_EUREKA_URI}/user-setting/package/management`;
                } else {
                    // CN 环境移除 version 菜单项（订阅管理）
                    _items = _items.filter(item => item.key !== 'version');
                }
            }
            _items = _items.filter(item => !nonePackages.includes(item.key as string));
        }

        if (isCreditsAvailable.value) {
            // 添加积分菜单项，跳转到credits页面
            // 积分菜单项在usage之后,如果usage不存在，则添加到最前面
            const creditsMenuItem = {
                key: 'credits',
                label: ({
                    cn: '积分',
                    en: 'Credits',
                    jp: '積分',
                    tw: '積分',
                })[locale],
                href: `${import.meta.env.VITE_EUREKA_URI}/credits`,
                icon: CreditsIcon,
            };
            const usageItemIndex = _items.findIndex(item => item.key === 'package');
            // 若存在'package'，则插到其后，否则插到最前面
            usageItemIndex !== -1
                ? _items.splice(usageItemIndex + 1, 0, creditsMenuItem)
                : _items.unshift(creditsMenuItem);
        }
        if (!isCountPackageAvailable.value) {
            // 删除count_package菜单项
            const countPackageItemIndex = _items.findIndex(item => item.key === 'package');
            if (countPackageItemIndex !== -1) {
                return _items.filter(item => item.key !== 'package');
            }
        }
        return _items;
    }
}
